import type { SourcingMetricsFilterRange } from 'api-clients/monolith';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export const METRICS_FILTER_RANGE_VALUES: SourcingMetricsFilterRange[] = [
  '7',
  '15',
  '30',
  '90',
  'lifetime',
];

const START_DATE_PARAM = 'start_date';
const END_DATE_PARAM = 'end_date';
const FILTER_RANGE_PARAM = 'filter_range';
const HIRING_GOAL_ID_PARAM = 'hiring_goal_id';

export const DEFAULT_METRICS_FILTER_RANGE: SourcingMetricsFilterRange =
  'lifetime';

function parseFilterRange(
  value: SourcingMetricsFilterRange | null,
): SourcingMetricsFilterRange | undefined {
  if (!value) return undefined;
  return METRICS_FILTER_RANGE_VALUES.includes(value) ? value : undefined;
}

export type MetricsFilterUpdate = {
  startDate?: string;
  endDate?: string;
  filterRange?: SourcingMetricsFilterRange;
};

/**
 * Metrics window from URL search params (`start_date`, `end_date`, `filter_range`).
 * When unset, callers omit them so the API uses hiring-goal dates or org defaults.
 */
export function useMetricsFilterParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const { startDate, endDate, filterRange } = useMemo(() => {
    const start = searchParams.get(START_DATE_PARAM) ?? undefined;
    const end = searchParams.get(END_DATE_PARAM) ?? undefined;
    const range = parseFilterRange(
      searchParams.get(FILTER_RANGE_PARAM) as SourcingMetricsFilterRange | null,
    );

    return { startDate: start, endDate: end, filterRange: range };
  }, [searchParams]);

  const setMetricsFilter = useCallback(
    (update: MetricsFilterUpdate) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete(HIRING_GOAL_ID_PARAM);
        if ('startDate' in update) {
          if (update.startDate) next.set(START_DATE_PARAM, update.startDate);
          else next.delete(START_DATE_PARAM);
        }
        if ('endDate' in update) {
          if (update.endDate) next.set(END_DATE_PARAM, update.endDate);
          else next.delete(END_DATE_PARAM);
        }
        if ('filterRange' in update) {
          if (update.filterRange) {
            next.set(FILTER_RANGE_PARAM, update.filterRange);
          } else {
            next.delete(FILTER_RANGE_PARAM);
          }
        }

        return next;
      });
    },
    [setSearchParams],
  );

  const clearMetricsFilter = useCallback(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete(START_DATE_PARAM);
      next.delete(END_DATE_PARAM);
      next.delete(FILTER_RANGE_PARAM);
      next.delete(HIRING_GOAL_ID_PARAM);
      return next;
    });
  }, [setSearchParams]);

  const applyFilterRange = useCallback(
    (range: SourcingMetricsFilterRange) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set(FILTER_RANGE_PARAM, range);
        next.delete(START_DATE_PARAM);
        next.delete(END_DATE_PARAM);
        next.delete(HIRING_GOAL_ID_PARAM);
        return next;
      });
    },
    [setSearchParams],
  );

  return {
    startDate,
    endDate,
    filterRange,
    setMetricsFilter,
    clearMetricsFilter,
    applyFilterRange,
  };
}
